import React, { memo, useState, useEffect } from 'react';

import {
  IconArrowDown01,
  IconInsta01,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import { useRouter } from 'next/navigation';
import { isAndroid, isIOS } from 'react-device-detect';
import { addAsIsMWebDomain } from '@wconcept/wconcept-common/utils/domainUtil';
import type { AgentOnBoardingYn } from '@wconcept/wconcept-common/models/renewal/datadog';
import * as S from './style';

interface FooterProps {
  isLogin: boolean;
  onClickFooter?: (type: string) => void;
  agentOnBoardingYn?: AgentOnBoardingYn;
}

const Footer = ({ isLogin, onClickFooter, agentOnBoardingYn }: FooterProps) => {
  const router = useRouter();
  const [logoutUrl, setLogoutUrl] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [customerUrl, setCustomerUrl] = useState('');
  const [marketingUrl, setMarketingUrl] = useState('');
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    const fetchUrls = async () => {
      const logout = await addAsIsMWebDomain('/Member/Logout');
      const login = await addAsIsMWebDomain('/Member/Login');
      const company = await addAsIsMWebDomain('/Etc/Company');
      const customer = await addAsIsMWebDomain('/CustomerCenter');
      const marketing = await addAsIsMWebDomain('/Etc/Marketting');
      setLoginUrl(login);
      setLogoutUrl(logout);
      setCompanyUrl(company);
      setCustomerUrl(customer);
      setMarketingUrl(marketing);
    };

    fetchUrls();
  }, [isMounted]);

  const onClickAppDownload = () => {
    onClickFooter?.('앱설치');
    if (isIOS) {
      const intentUrl = `wconceptkr://${window.location.host}${window.location.pathname}`;
      const fallbackUrl = 'https://apps.apple.com/kr/app/id968266068';

      // 앱 실행 시도
      window.location.href = intentUrl;

      // 2초 후 앱스토어로 이동 (앱이 설치되어 있지 않은 경우)
      setTimeout(() => {
        if (document.hidden) {
          // 앱이 실행된 경우 아무 작업도 하지 않음
          return;
        }
        // 앱이 설치되어 있지 않은 경우
        window.location.href = fallbackUrl;
      }, 2000);
    } else if (isAndroid) {
      // Android 로직 수정
      const intentUrl =
        'intent://view/#Intent;scheme=wconceptkr;package=com.looket.wconcept;end';
      const fallbackUrl =
        'https://play.google.com/store/apps/details?id=com.looket.wconcept&hl=ko';
      // 앱 실행 시도
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = intentUrl;
      document.body.appendChild(iframe);

      // 2초 후 앱 스토어로 이동 (앱이 설치되어 있지 않은 경우)
      setTimeout(() => {
        if (document.hidden) {
          // 앱이 실행된 경우
          document.body.removeChild(iframe);
        } else {
          // 앱이 설치되어 있지 않은 경우
          window.location.href = fallbackUrl;
        }
      }, 2000);
    } else {
      // 기타 플랫폼은 그대로 유지
      router.replace(
        'https://play.google.com/store/apps/details?id=com.looket.wconcept&hl=ko'
      );
    }
  };

  return (
    <S.Footer id="footer">
      <div className="inner-footer">
        <ul className="nav-util">
          {isLogin && (
            <li>
              <a href={logoutUrl} onClick={() => onClickFooter?.('로그아웃')}>
                로그아웃
              </a>
            </li>
          )}
          {!isLogin && (
            <li>
              <a href={loginUrl} onClick={() => onClickFooter?.('로그인')}>
                로그인
              </a>
            </li>
          )}
          <li>
            <a href={companyUrl}>회사소개</a>
          </li>
          <li>
            <a href={marketingUrl}>제휴문의</a>
          </li>
          <li>
            <a
              href="https://ads.wconcept.co.kr/"
              target="_blank"
              rel="noreferrer"
            >
              광고안내
            </a>
          </li>
          <li>
            <a
              href="https://wconceptpartner.co.kr/"
              target="_blank"
              rel="noreferrer"
            >
              파트너라운지
            </a>
          </li>
          <li>
            <button type="button" onClick={onClickAppDownload}>
              앱설치
            </button>
          </li>
        </ul>
        <details className="info">
          <summary className="inf-sum">
            ㈜더블유컨셉코리아 사업자 정보 <IconArrowDown01 />
          </summary>
          <ul className="inf-list">
            <li>대표자 : 이지은</li>
            <li>
              주소 : 서울특별시 영등포구 영신로34길 30, 1~2층 (영등포동,
              KB영등포타워)
            </li>
            <li>사업자등록번호 : 211-88-19183</li>
            <li>
              통신판매업신고 : 제2025-서울영등포-0492호{' '}
              <a
                href="https://www.ftc.go.kr/bizCommPop.do?wrkr_no=2118819183&apv_perm_no="
                target="_blank"
                rel="noreferrer"
              >
                사업자정보확인
              </a>
            </li>
            <li>개인정보보호책임자 : 허선희</li>
            <li>호스팅서비스 : ㈜더블유컨셉코리아</li>
            <li>
              고객센터 :{' '}
              <a className="tel" href="tel:1566-5027">
                1566-5027
              </a>
            </li>
            <li>
              이메일 :{' '}
              <a
                className="tel"
                href="mailto:cs_help@wconcept.co.kr"
                target="_blank"
                rel="noreferrer"
              >
                cs_help@wconcept.co.kr
              </a>
            </li>
            <li>운영시간 : 평일 09:00~18:00 (점심시간 : 12:30~13:30)</li>
          </ul>
        </details>
        <ul className="nav-terms">
          <li>
            <a href="https://m.wconcept.co.kr/CustomerCenter/News">공지사항</a>
          </li>
          <li>
            <a href={customerUrl}>고객센터</a>
          </li>
          <li>
            <a href="https://m.wconcept.co.kr/CustomerAgreement">이용약관</a>
          </li>
          {agentOnBoardingYn === 'Y' && (
            <li>
              <a href="https://m.wconcept.co.kr/LocationAgreement">
                <strong>위치정보이용약관</strong>
              </a>
            </li>
          )}
          <li>
            <a href="https://m.wconcept.co.kr/CustomerPrivacy">
              <strong>개인정보처리방침</strong>
            </a>
          </li>
        </ul>
        <p className="copyright">
          COPYRIGHT ⓒ ㈜더블유컨셉코리아 <br /> ALL RIGHTS RESERVED.
        </p>
        <div className="desc">
          (주)더블유컨셉코리아는 W컨셉에 입점한 개별 판매자가 판매하는 상품에
          대해서는 통신판매중개자로서 통신판매의 당사자가 아니며, 입점 판매자가
          등록한 상품정보 및 거래 등에 대해 책임을 지지 않습니다. 개별 상품
          페이지 등에서 해당 판매자 정보를 확인하시기 바랍니다.
        </div>
        <ul className="nav-social">
          <li>
            <a href="//www.instagram.com/wconceptkorea/">
              <IconInsta01 />
            </a>
          </li>
        </ul>
      </div>
    </S.Footer>
  );
};

export default memo(Footer);
